import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  NavigateFunction,
  NavigateOptions,
  To,
  useNavigate
} from 'react-router-dom';

import { UnsavedChangesModal } from 'components/shared/Modal/UnsavedChangesModal';

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  requestNavigation: (action: () => void) => void;
}

const defaultContext: UnsavedChangesContextType = {
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
  requestNavigation: (action) => action()
};

export const UnsavedChangesContext =
  createContext<UnsavedChangesContextType>(defaultContext);

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export const UnsavedChangesProvider = ({
  children
}: UnsavedChangesProviderProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        pendingNavigationRef.current = action;
        setIsModalOpen(true);
        return;
      }
      action();
    },
    [hasUnsavedChanges]
  );

  const handleContinueEditing = useCallback(() => {
    pendingNavigationRef.current = null;
    setIsModalOpen(false);
  }, []);

  const handleDiscard = useCallback(() => {
    const action = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setIsModalOpen(false);
    action?.();
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const contextValue = useMemo(
    () => ({ hasUnsavedChanges, setHasUnsavedChanges, requestNavigation }),
    [hasUnsavedChanges, requestNavigation]
  );

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
      <UnsavedChangesModal
        isOpen={isModalOpen}
        onContinueEditing={handleContinueEditing}
        onDiscard={handleDiscard}
      />
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = (): UnsavedChangesContextType =>
  useContext(UnsavedChangesContext);

export const useGuardedNavigate = (): NavigateFunction => {
  const navigate = useNavigate();
  const { requestNavigation } = useUnsavedChanges();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      requestNavigation(() => {
        void navigate(to, options);
      });
    },
    [navigate, requestNavigation]
  ) as NavigateFunction;
};
